/** @format */

import { ChangeDetectionStrategy, Component, computed, inject, input, model } from '@angular/core';
import { CommonModule, formatCurrency } from '@angular/common';
import {
	HmfAlertBannerComponent,
	HmfSelectComponent,
	HmfSelectorComponent,
	HmfSkeletonComponent,
	HmfTextLinkComponent
} from '@dsg-tech/homefield-angular';
import { Warranty } from '@shared/data-access/interfaces';
import { NoPlanKey, NoPlanLabel } from './warranty-plan-selector.constants';
import { WarrantyDropdownOption } from '@pdp/data-access/interfaces';

import { AnalyticsService } from '@shared/data-access/services';
import { HomefieldIconLibraryModule } from '@dsg-tech/homefield-angular-icons';
import { Store } from '@ngrx/store';
import { selectProductPartNumber } from '@pdp/data-access/state';
import { FormsModule } from '@angular/forms';

@Component({
	selector: 'pdp-component-warranty-plan-selector',
	imports: [
		CommonModule,
		HmfSkeletonComponent,
		HmfSelectorComponent,
		HmfSelectComponent,
		HmfAlertBannerComponent,
		HmfTextLinkComponent,
		HomefieldIconLibraryModule,
		FormsModule
	],
	templateUrl: './warranty-plan-selector.component.html',
	styleUrl: './warranty-plan-selector.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class WarrantyPlanSelectorComponent {
	analyticsService = inject(AnalyticsService);
	store = inject(Store);

	productEcode = this.store.selectSignal(selectProductPartNumber);

	isLoading = input.required<boolean>();
	isError = input.required<boolean>();
	warranties = input.required<Warranty[]>();
	// undefined -> no selection yet, null -> no plan selected
	readonly selectedPlanSku = model.required<string | undefined | null>();

	readonly hmfSelectOptions = computed(() => {
		if (this.warranties().length < 4) {
			return [];
		}
		return this.warranties()
			.map((warranty) => ({
				key: warranty.planSku.toString(),
				label: this.getHmfSelectLabel(warranty),
				selected: warranty.planSku === this.selectedPlanSku(),
				planEcode: warranty.planEcode
			}))
			.concat({
				key: NoPlanKey,
				label: NoPlanLabel,
				selected: this.selectedPlanSku() === null,
				planEcode: null
			});
	});

	readonly selectedPlanLabelForHmfSelect = computed(() => {
		if (this.selectedPlanSku() === null) {
			return NoPlanLabel;
		}
		const selectedHmfSelectOption = this.hmfSelectOptions().find((opt) => opt.selected);
		return selectedHmfSelectOption?.label ?? '';
	});

	setSelectedPlanSku(warranty: Warranty) {
		this.selectedPlanSku.set(warranty.planSku);
		this.analyticsService.triggerAnalyticsEvent('WarrantySkuResolve', {
			ProductID: this.productEcode(),
			WarrantyEcode: warranty.planEcode
		});
	}

	private getHmfSelectLabel(warranty: Warranty) {
		const priceString = warranty.sellingPrice
			? ` - ${formatCurrency(warranty.sellingPrice, 'en-US', '$')}`
			: '';
		return `${warranty.planDisplayName}${priceString}`;
	}

	handleHmfSelectChange(selectedValue: WarrantyDropdownOption) {
		if (selectedValue.key === NoPlanKey) {
			this.selectedPlanSku.set(null);
		} else {
			this.selectedPlanSku.set(selectedValue.key);
			this.analyticsService.triggerAnalyticsEvent('WarrantySkuResolve', {
				ProductID: this.productEcode(),
				WarrantyEcode: selectedValue.planEcode
			});
		}
	}
}
